/**
 * O MAPA CENTRAL DAS ETIQUETAS DE ESTADO.
 *
 * POR QUE ISTO EXISTE
 *
 *   Cada tela decidia sozinha a cor da própria pílula, e o resultado foi o que
 *   o dono viu nos prints de 04/09/2026: "Frio" cinza em toda linha da tabela
 *   de contatos, "Rascunho" e "Inativa" cinzas, "acesso direto", "desktop",
 *   "mobile" e "veio de localhost:5273" cinzas na jornada, "Formulário site"
 *   cinza. Cinza é a pílula neutra do sistema - "sem juízo de valor"
 *   (componentes.html §04.8) - e nenhuma dessas palavras é neutra: cada uma
 *   carrega um significado que o sistema já tem cor e ícone para dizer.
 *
 *   Aqui vive a decisão, uma vez. A tela pergunta "qual é a etiqueta de
 *   `cold`?" e recebe `{ tom, icone, rotulo }`; ela não escolhe cor.
 *
 * AS REGRAS, TRANSCRITAS DO DESIGN SYSTEM
 *
 *   componentes.html §04.8 - o que cada tom SIGNIFICA:
 *     neutro   "sem juízo de valor"                       Rascunho, Arquivado
 *     marca    "pertence à plataforma"                    Plano, Indicação
 *     ok       "terminou bem"                             Publicada, Pago
 *     atenção  "vai dar problema se ninguém agir"         Pausada, Aguardando
 *     erro     "já deu problema"                          Falhou, Suspenso
 *     info     "está acontecendo agora, sem ação"         Enviando, Agendada
 *     contorno "metadado discreto"                        Importado do CSV
 *
 *   fundamentos.html §01.3 - "cor nunca vem sozinha: todo estado carrega
 *   ícone e palavra". Por isso cada etiqueta daqui traz o ícone junto, e a
 *   pílula neutra, que antes era só texto, agora tem o ícone do que ela é
 *   (lápis no rascunho, globo no acesso direto, celular no dispositivo).
 *
 *   produto.html - temperatura de lead na coluna do CRM: "Quente" é a
 *   pílula forte, "6 dias parada" é atenção. graficos.html - origem colore
 *   pela família do canal: conversa é ciano em todo o ecossistema
 *   (produto.html §"ícone do WhatsApp em ciano"), marketing é roxo, e o que a
 *   plataforma indica (indicação) é a marca.
 *
 * COMO ESTENDER
 *
 *   Estado novo entra no mapa da função certa, com tom, ícone e rótulo. Não
 *   existe "cai no cinza": o que não está aqui volta como neutro SEM ícone e
 *   com o próprio texto, e `tests/etiquetas.test.ts` lista os estados
 *   conhecidos para que nenhum deles caia aí por acidente.
 */

import { Icone, type ComponenteDeIcone } from './icones';
import { varianteDaEtiqueta, type VarianteDePilula } from './vocabulario';

/**
 * Os tons. Os sete do design system mais dois que o produto precisou:
 * `roxo`, a família do Marketing, para origem e canal de e-mail/formulário/
 * campanha (componentes.html §04.8 manda origem e canal para o contorno, mas
 * o dono pediu a cor da família do canal, e roxo é a do Marketing); e
 * `amarelo`, o amarelo Yard cravado, para a indicação: `brand` segue a
 * família do produto em que a tela está (roxo dentro do Marketing, ciano no
 * Flow), e indicação é amarela em qualquer tela; e `escuro`, o "neutro
 * escuro" do arquivado, que precisa se separar do rascunho sem virar juízo
 * de valor.
 */
export type TomDeEtiqueta =
  | 'brand'
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'outline'
  | 'roxo'
  | 'amarelo'
  | 'escuro';

export type Etiqueta = {
  tom: TomDeEtiqueta;
  /** O ícone que acompanha a palavra. Sem ele, a pílula colorida mostra o ponto. */
  icone?: ComponenteDeIcone;
  rotulo: string;
};

export const TONS_DE_ETIQUETA: TomDeEtiqueta[] = [
  'neutral', 'brand', 'success', 'warning', 'danger', 'info', 'outline', 'roxo', 'amarelo', 'escuro',
];

/** Gênero do substantivo que a etiqueta qualifica: "Publicada" (página) ou "Publicado" (quiz). */
export type Genero = 'f' | 'm';

const semAcento = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const chave = (s: string | null | undefined) => semAcento(String(s ?? '').trim().toLowerCase());
const flexionar = (modelo: string, genero: Genero) => modelo.replace(/\{a\}/g, genero === 'm' ? 'o' : 'a');
const capitalizar = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/* ------------------------------------------------------------------ */
/* Temperatura do lead                                                  */
/* ------------------------------------------------------------------ */

/**
 * Quente, morno e frio, e as três situações do contato do Marketing (`ready`,
 * `nurturing`, `cold`), que são a mesma régua com outros nomes.
 *
 * Quente é erro no sentido do sistema: "já está acontecendo, ligue hoje";
 * morno é atenção, "vai esfriar se ninguém agir"; frio é info, a faixa fria
 * da régua (tela-flow.html §02 sempre pintou "frio" em ciano). O termômetro
 * é o ícone dos três porque é a mesma medida em três alturas; "Pronto" não é
 * temperatura, é conclusão, e leva o check.
 */
const TEMPERATURA: Record<string, Etiqueta> = {
  quente: { tom: 'danger', icone: Icone.Termometro, rotulo: 'Quente' },
  hot: { tom: 'danger', icone: Icone.Termometro, rotulo: 'Quente' },
  morno: { tom: 'warning', icone: Icone.Termometro, rotulo: 'Morno' },
  warm: { tom: 'warning', icone: Icone.Termometro, rotulo: 'Morno' },
  nurturing: { tom: 'warning', icone: Icone.Termometro, rotulo: 'Nutrindo' },
  nutrindo: { tom: 'warning', icone: Icone.Termometro, rotulo: 'Nutrindo' },
  frio: { tom: 'info', icone: Icone.Termometro, rotulo: 'Frio' },
  cold: { tom: 'info', icone: Icone.Termometro, rotulo: 'Frio' },
  ready: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Pronto' },
  pronto: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Pronto' },
};

export function etiquetaDeTemperatura(valor: string | null | undefined): Etiqueta {
  const k = chave(valor).split('·')[0]?.trim() ?? '';
  return TEMPERATURA[k] ?? { tom: 'outline', rotulo: valor ? capitalizar(String(valor)) : 'Sem temperatura' };
}

/* ------------------------------------------------------------------ */
/* Status de publicação, ativação e envio                               */
/* ------------------------------------------------------------------ */

type ModeloDeStatus = { tom: TomDeEtiqueta; icone?: ComponenteDeIcone; rotulo: string };

/** `{a}` vira "a" ou "o" conforme o gênero pedido. */
const STATUS: Record<string, ModeloDeStatus> = {
  /* terminou bem */
  published: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Publicad{a}' },
  publicada: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Publicad{a}' },
  publicado: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Publicad{a}' },
  live: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'No ar' },
  'no ar': { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'No ar' },
  active: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Ativ{a}' },
  ativa: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Ativ{a}' },
  ativo: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Ativ{a}' },
  sent: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Enviad{a}' },
  enviado: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Enviad{a}' },
  verified: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Verificad{a}' },
  connected: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Conectad{a}' },
  approved: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Aprovad{a}' },
  confirmed: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Confirmad{a}' },
  resolved: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Resolvid{a}' },
  paid: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Pago' },
  done: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Realizad{a}' },
  'for sale': { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'À venda' },

  /* sem juízo de valor */
  draft: { tom: 'neutral', icone: Icone.Rascunho, rotulo: 'Rascunho' },
  rascunho: { tom: 'neutral', icone: Icone.Rascunho, rotulo: 'Rascunho' },
  offline: { tom: 'neutral', icone: Icone.SemSinal, rotulo: 'Fora do ar' },
  'fora do ar': { tom: 'neutral', icone: Icone.SemSinal, rotulo: 'Fora do ar' },
  hidden: { tom: 'neutral', icone: Icone.OlhoFechado, rotulo: 'Ocult{a}' },
  oculto: { tom: 'neutral', icone: Icone.OlhoFechado, rotulo: 'Ocult{a}' },
  visible: { tom: 'success', icone: Icone.Olho, rotulo: 'Visível' },
  'in review': { tom: 'outline', icone: Icone.Olho, rotulo: 'Em revisão' },
  'em revisao': { tom: 'outline', icone: Icone.Olho, rotulo: 'Em revisão' },

  /* neutro escuro: guardado, ninguém mexe */
  archived: { tom: 'escuro', icone: Icone.Arquivar, rotulo: 'Arquivad{a}' },
  arquivada: { tom: 'escuro', icone: Icone.Arquivar, rotulo: 'Arquivad{a}' },
  arquivado: { tom: 'escuro', icone: Icone.Arquivar, rotulo: 'Arquivad{a}' },

  /* vai dar problema se ninguém agir */
  paused: { tom: 'warning', icone: Icone.Pausa, rotulo: 'Pausad{a}' },
  pausada: { tom: 'warning', icone: Icone.Pausa, rotulo: 'Pausad{a}' },
  pausado: { tom: 'warning', icone: Icone.Pausa, rotulo: 'Pausad{a}' },
  inactive: { tom: 'warning', icone: Icone.Pausa, rotulo: 'Inativ{a}' },
  inativa: { tom: 'warning', icone: Icone.Pausa, rotulo: 'Inativ{a}' },
  inativo: { tom: 'warning', icone: Icone.Pausa, rotulo: 'Inativ{a}' },
  disabled: { tom: 'warning', icone: Icone.Pausa, rotulo: 'Desativad{a}' },
  off: { tom: 'warning', icone: Icone.Pausa, rotulo: 'Desligad{a}' },
  desligado: { tom: 'warning', icone: Icone.Pausa, rotulo: 'Desligad{a}' },
  pending: { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Aguardando' },
  waiting: { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Aguardando' },
  aguardando: { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Aguardando' },
  'not configured': { tom: 'warning', icone: Icone.Engrenagem, rotulo: 'Falta configurar' },

  /* já deu problema */
  error: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Erro' },
  erro: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Erro' },
  failed: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Falhou' },
  rejected: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Reprovad{a}' },
  suspended: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Suspens{a}' },
  canceled: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Cancelad{a}' },
  cancelled: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Cancelad{a}' },
  blocked: { tom: 'danger', icone: Icone.Cadeado, rotulo: 'Bloquead{a}' },

  /* acontecendo agora, sem ação do usuário */
  scheduled: { tom: 'info', icone: Icone.Calendario, rotulo: 'Agendad{a}' },
  agendada: { tom: 'info', icone: Icone.Calendario, rotulo: 'Agendad{a}' },
  agendado: { tom: 'info', icone: Icone.Calendario, rotulo: 'Agendad{a}' },
  sending: { tom: 'info', icone: Icone.Disparo, rotulo: 'Enviando' },
  running: { tom: 'info', icone: Icone.Automacao, rotulo: 'Rodando' },
  open: { tom: 'info', icone: Icone.Negocio, rotulo: 'Abert{a}' },
  reserved: { tom: 'info', icone: Icone.Calendario, rotulo: 'Reservad{a}' },
};

/**
 * A etiqueta de um status de publicação, ativação ou envio.
 *
 * `genero` flexiona o rótulo: `etiquetaDeStatus('published')` é "Publicada"
 * (página, campanha, automação); `etiquetaDeStatus('published', 'm')` é
 * "Publicado" (quiz, chatbot, site).
 */
export function etiquetaDeStatus(status: string | null | undefined, genero: Genero = 'f'): Etiqueta {
  const k = chave(status).replace(/[_-]+/g, ' ');
  const m = STATUS[k];
  if (m) return { tom: m.tom, icone: m.icone, rotulo: flexionar(m.rotulo, genero) };
  return { tom: 'neutral', rotulo: status ? capitalizar(String(status)) : 'Sem status' };
}

/* ------------------------------------------------------------------ */
/* Origem (primeiro toque) e canal                                      */
/* ------------------------------------------------------------------ */

/**
 * O host legível de um referrer: sem protocolo, sem `www.`, sem caminho e
 * sem porta. Volta vazio quando o referrer é a própria máquina de quem
 * desenvolve (`localhost:5273`, `127.0.0.1`) - em produção isso era lido
 * como "veio de localhost:5273", que não é origem de ninguém: é acesso direto.
 */
export function hostDeOrigem(referrer: string | null | undefined): string {
  if (!referrer) return '';
  const host = referrer
    .trim()
    .replace(/^[a-z]+:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/[/?#].*$/, '')
    .replace(/:\d+$/, '')
    .toLowerCase();
  if (!host || host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]' || host.endsWith('.local')) return '';
  return host;
}

export const ACESSO_DIRETO = 'Acesso direto';

/**
 * Palavras que chegam gravadas como chave, sem acento (`formulario`,
 * `organico`), e que a tela escrevia assim: "Formulario" ao lado de
 * "Formulário" no mesmo gráfico (achado da F5). Só troca palavra inteira e
 * só a que está aqui; nome de site ou de campanha passa como veio.
 */
const ACENTOS: Record<string, string> = {
  formulario: 'formulário', formularios: 'formulários', organico: 'orgânico', organica: 'orgânica',
  indicacao: 'indicação', importacao: 'importação', pagina: 'página', anuncio: 'anúncio', anuncios: 'anúncios',
};
/** `landing`, `landing_page`, `lp`, `email`: minúscula, sem espaço, sem ponto. */
const eChaveCrua = (s: string) => /^[a-z0-9]+([_-][a-z0-9]+)*$/.test(s);

const comAcento = (s: string) => /[./:]/.test(s) ? s : s.replace(/[a-z]+/gi, (p) => {
  const certo = ACENTOS[p.toLowerCase()];
  return certo ? (p[0] === p[0]!.toUpperCase() ? capitalizar(certo) : certo) : p;
});

/**
 * `rotulo` vale sempre; `nomeDaChave` só quando o valor gravado é uma chave
 * crua (`landing`, `lp`, `email`, `cpc`): texto escrito por gente, como
 * "google · cpc" ou "Landing do lançamento", passa como veio. Antes a chave
 * saía só capitalizada, "Landing" ao lado de "Landing page" (achado da F6).
 */
type RegraDeOrigem = { casa: RegExp; tom: TomDeEtiqueta; icone: ComponenteDeIcone; rotulo?: string; nomeDaChave?: string };

/**
 * A ordem importa: "google ads" é campanha (roxo) antes de ser busca (neutro),
 * "formulário do site" é formulário (roxo) antes de ser site (globo).
 */
const ORIGENS: RegraDeOrigem[] = [
  { casa: /whatsapp|\bwa\b|wpp/, tom: 'info', icone: Icone.Whatsapp, rotulo: 'WhatsApp' },
  { casa: /pop-?up|chat|conversa/, tom: 'info', icone: Icone.Balao, nomeDaChave: 'Pop-up Flow' },
  { casa: /indica|referral|recomend|boca a boca/, tom: 'amarelo', icone: Icone.Publico, rotulo: 'Indicação' },
  { casa: /\bads?\b|cpc|paid|paga|pago|campanha|campaign|patrocin|meta ads|display/, tom: 'roxo', icone: Icone.Campanha, nomeDaChave: 'Anúncio pago' },
  { casa: /instagram|facebook|linkedin|tiktok|youtube|twitter|x\.com|threads|social/, tom: 'roxo', icone: Icone.Campanha },
  { casa: /e-?mail|newsletter|mailchimp|resend/, tom: 'roxo', icone: Icone.EMail, nomeDaChave: 'E-mail' },
  { casa: /formul|\bform\b/, tom: 'roxo', icone: Icone.Formulario, nomeDaChave: 'Formulário' },
  { casa: /landing|\blp\b/, tom: 'roxo', icone: Icone.Formulario, nomeDaChave: 'Landing page' },
  { casa: /csv|import|planilha|xlsx/, tom: 'outline', icone: Icone.UploadNuvem, rotulo: 'Importação CSV' },
  { casa: /manual|cadastro|na mao/, tom: 'outline', icone: Icone.Editar, rotulo: 'Manual' },
  { casa: /evento|feira|palestra|webinar/, tom: 'outline', icone: Icone.Calendario },
  { casa: /myard|\bbio\b|link na bio/, tom: 'neutral', icone: Icone.LinkBio },
  { casa: /google|bing|duckduckgo|yahoo|busca|organic|organica|seo/, tom: 'neutral', icone: Icone.Busca },
  { casa: /acesso direto|direct|\(direct\)|\(none\)/, tom: 'neutral', icone: Icone.Globo, rotulo: ACESSO_DIRETO },
];

/**
 * A etiqueta da origem do contato ou da visita: "de onde veio", no primeiro
 * toque. Aceita o texto gravado em `contacts.source` ("Indicação",
 * "Formulário do site", "WhatsApp"), um `utm_source`, ou um host de
 * referrer já limpo por `hostDeOrigem`. Vazio é acesso direto.
 *
 * O rótulo devolvido é o texto original (a tela não perde "google · cpc"),
 * exceto nos canais com nome canônico (WhatsApp, Indicação, Manual, CSV).
 */
export function etiquetaDeOrigem(origem: string | null | undefined): Etiqueta {
  let texto = String(origem ?? '').trim().replace(/^veio d[eo]\s+/i, '');
  /* Uma URL ou um host (`https://www.exemplo.com.br/pagina`, `localhost:5273`)
     vira o host limpo; a máquina de quem desenvolve vira acesso direto. */
  if (/^([a-z]+:\/\/)?[a-z0-9.-]+(:\d+)?(\/\S*)?$/i.test(texto) && (texto.includes('.') || /localhost|:\d+/.test(texto))) {
    texto = hostDeOrigem(texto);
  }
  const k = chave(texto);
  if (!k) return { tom: 'neutral', icone: Icone.Globo, rotulo: ACESSO_DIRETO };
  for (const r of ORIGENS) {
    if (r.casa.test(k)) return { tom: r.tom, icone: r.icone, rotulo: r.rotulo ?? (r.nomeDaChave && eChaveCrua(texto) ? r.nomeDaChave : capitalizar(comAcento(texto))) };
  }
  /* Um host (`exemplo.com.br`) ou uma palavra que o cliente inventou: é o
     site de onde a pessoa veio. Globo, neutro, texto como está. */
  return { tom: 'neutral', icone: Icone.Globo, rotulo: capitalizar(comAcento(texto)) };
}

/** A etiqueta de "veio de": o host do referrer, ou acesso direto quando não há um. */
export function etiquetaDeReferrer(referrer: string | null | undefined): Etiqueta {
  return etiquetaDeOrigem(hostDeOrigem(referrer));
}

type RegraDeCanal = { casa: RegExp; tom: TomDeEtiqueta; icone: ComponenteDeIcone; rotulo: string };

const CANAIS: RegraDeCanal[] = [
  { casa: /whatsapp|\bwa\b|wpp/, tom: 'info', icone: Icone.Whatsapp, rotulo: 'WhatsApp' },
  { casa: /e-?mail/, tom: 'roxo', icone: Icone.EMail, rotulo: 'E-mail' },
  { casa: /formul|\bform\b/, tom: 'roxo', icone: Icone.Formulario, rotulo: 'Formulário' },
  { casa: /pop-?up|chat|site|web/, tom: 'info', icone: Icone.Balao, rotulo: 'Chat' },
  { casa: /telefone|phone|liga|voz|call/, tom: 'neutral', icone: Icone.Telefone, rotulo: 'Telefone' },
  { casa: /sms/, tom: 'neutral', icone: Icone.Balao, rotulo: 'SMS' },
  { casa: /reuniao|meeting|video/, tom: 'neutral', icone: Icone.SalaVideo, rotulo: 'Reunião' },
  { casa: /sistema|system|interno|internal|app/, tom: 'neutral', icone: Icone.Engrenagem, rotulo: 'Sistema' },
];

/**
 * A etiqueta do canal por onde a conversa, a mensagem ou a tarefa acontece.
 * Conversa é ciano em todo o ecossistema; e-mail e formulário são do
 * Marketing, roxo; telefone e sistema não têm família, neutro com ícone.
 */
export function etiquetaDeCanal(canal: string | null | undefined): Etiqueta {
  const texto = String(canal ?? '').trim();
  const k = chave(texto);
  if (!k) return { tom: 'neutral', icone: Icone.Engrenagem, rotulo: 'Sistema' };
  for (const r of CANAIS) {
    if (r.casa.test(k)) return { tom: r.tom, icone: r.icone, rotulo: r.rotulo };
  }
  return { tom: 'neutral', rotulo: capitalizar(texto) };
}

/* ------------------------------------------------------------------ */
/* Dispositivo                                                          */
/* ------------------------------------------------------------------ */

/**
 * Celular, tablet ou computador. Sempre neutro: o dispositivo não é bom nem
 * ruim, e o que o distingue é o ícone, não a cor.
 */
export function etiquetaDeDispositivo(dispositivo: string | null | undefined): Etiqueta {
  const k = chave(dispositivo);
  if (!k) return { tom: 'neutral', icone: Icone.Monitor, rotulo: 'Dispositivo desconhecido' };
  if (/tablet|ipad/.test(k)) return { tom: 'neutral', icone: Icone.Celular, rotulo: 'Tablet' };
  if (/mobile|celular|phone|android|iphone|smartphone/.test(k)) return { tom: 'neutral', icone: Icone.Celular, rotulo: 'Celular' };
  if (/desktop|computador|pc|mac|windows|linux|notebook|laptop/.test(k)) return { tom: 'neutral', icone: Icone.Monitor, rotulo: 'Computador' };
  return { tom: 'neutral', icone: Icone.Monitor, rotulo: capitalizar(String(dispositivo)) };
}

/* ------------------------------------------------------------------ */
/* Estados que o produto tem além de publicação: a segunda rodada       */
/* ------------------------------------------------------------------ */

/*
 * Na primeira rodada (04/09/2026) o mapa cobriu temperatura, status de
 * publicação, origem, canal e dispositivo, e o dono viu no funil do CRM que
 * o resto continuava cinza: "Indicação" sem ícone no cartão, "15 dias" parado,
 * o papel da pessoa nas configurações, o plano no console, o pedido da loja.
 * "Tem que estar em tudo, em todos os produtos." As funções abaixo são o
 * resto do vocabulário, uma por família de estado, todas com tom, ícone e
 * rótulo, no mesmo contrato das de cima.
 */

/* Mais status de publicação/ativação que apareceram na varredura. */
Object.assign(STATUS, {
  on: { tom: 'success', icone: Icone.Interruptor, rotulo: 'Ligad{a}' },
  ligado: { tom: 'success', icone: Icone.Interruptor, rotulo: 'Ligad{a}' },
  ligada: { tom: 'success', icone: Icone.Interruptor, rotulo: 'Ligad{a}' },
  configured: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Configurad{a}' },
  configurado: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Configurad{a}' },
  completed: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Concluíd{a}' },
  concluida: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Concluíd{a}' },
  concluido: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Concluíd{a}' },
  delivered: { tom: 'success', icone: Icone.Entrega, rotulo: 'Entregue' },
  entregue: { tom: 'success', icone: Icone.Entrega, rotulo: 'Entregue' },
  default: { tom: 'brand', icone: Icone.MarcadorEstrela, rotulo: 'Padrão' },
  padrao: { tom: 'brand', icone: Icone.MarcadorEstrela, rotulo: 'Padrão' },
  atual: { tom: 'brand', icone: Icone.MarcadorEstrela, rotulo: 'Atual' },
  'in progress': { tom: 'info', icone: Icone.Automacao, rotulo: 'Andando' },
  andando: { tom: 'info', icone: Icone.Automacao, rotulo: 'Andando' },
  'in approval': { tom: 'info', icone: Icone.Cronometro, rotulo: 'Em aprovação' },
  'em aprovacao': { tom: 'info', icone: Icone.Cronometro, rotulo: 'Em aprovação' },
  shipped: { tom: 'info', icone: Icone.Entrega, rotulo: 'Enviad{a}' },
  invited: { tom: 'warning', icone: Icone.EMail, rotulo: 'Convite pendente' },
  convidado: { tom: 'warning', icone: Icone.EMail, rotulo: 'Convite pendente' },
  unconfirmed: { tom: 'warning', icone: Icone.EMail, rotulo: 'E-mail não confirmado' },
  pendente: { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Pendente' },
  unsubscribed: { tom: 'warning', icone: Icone.OlhoFechado, rotulo: 'Saiu da lista' },
  descadastrou: { tom: 'warning', icone: Icone.OlhoFechado, rotulo: 'Saiu da lista' },
  'coming soon': { tom: 'outline', icone: Icone.Cronometro, rotulo: 'Em breve' },
  'em breve': { tom: 'outline', icone: Icone.Cronometro, rotulo: 'Em breve' },
  expired: { tom: 'escuro', icone: Icone.Cronometro, rotulo: 'Expirad{a}' },
  expirado: { tom: 'escuro', icone: Icone.Cronometro, rotulo: 'Expirad{a}' },
  revoked: { tom: 'escuro', icone: Icone.Cadeado, rotulo: 'Revogad{a}' },
  revogada: { tom: 'escuro', icone: Icone.Cadeado, rotulo: 'Revogad{a}' },
  removed: { tom: 'escuro', icone: Icone.Cadeado, rotulo: 'Removid{a}' },
  removido: { tom: 'escuro', icone: Icone.Cadeado, rotulo: 'Removid{a}' },
  refunded: { tom: 'warning', icone: Icone.MoedaReal, rotulo: 'Reembolsad{a}' },
  reembolsado: { tom: 'warning', icone: Icone.MoedaReal, rotulo: 'Reembolsad{a}' },
  cancelada: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Cancelad{a}' },
  cancelado: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Cancelad{a}' },
  falhou: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Falhou' },
  reprovada: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Reprovad{a}' },
  aprovada: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Aprovad{a}' },
  aprovado: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Aprovad{a}' },
  'sem consentimento': { tom: 'warning', icone: Icone.AlertaTriangulo, rotulo: 'Sem consentimento' },
  'dados insuficientes': { tom: 'outline', icone: Icone.InfoCirculo, rotulo: 'Dados insuficientes' },
  'em veiculacao': { tom: 'brand', icone: Icone.Campanha, rotulo: 'Em veiculação' },
  reaberto: { tom: 'warning', icone: Icone.Reabrir, rotulo: 'Reabert{a}' },
  reopened: { tom: 'warning', icone: Icone.Reabrir, rotulo: 'Reabert{a}' },
} satisfies Record<string, ModeloDeStatus>);

/**
 * Uma pílula de metadado: contagem, tipo, nome de caixa, "N passos". Sem
 * juízo de valor, contorno, e com o ícone do que ela é para não ficar muda
 * (fundamentos.html §01.3). É a saída para o que não é estado.
 */
export function etiquetaDeMetadado(rotulo: string, icone?: ComponenteDeIcone, tom: TomDeEtiqueta = 'outline'): Etiqueta {
  return { tom, icone, rotulo };
}

/* ------------------------------------------------------------------ */
/* Negócio, etapa, tempo parado, prioridade (CRM)                       */
/* ------------------------------------------------------------------ */

/**
 * Ganho, perdido ou aberto. Aberto mostra a etapa quando ela é dada
 * ("Proposta enviada"), porque é a etapa que responde "onde está".
 */
export function etiquetaDeNegocio(status: string | null | undefined, etapa?: string | null, genero: Genero = 'm'): Etiqueta {
  const k = chave(status);
  if (k === 'won' || k === 'ganho' || k === 'ganha') return { tom: 'success', icone: Icone.CheckCirculo, rotulo: flexionar('Ganh{a}', genero) };
  if (k === 'lost' || k === 'perdido' || k === 'perdida') return { tom: 'danger', icone: Icone.ErroCirculo, rotulo: flexionar('Perdid{a}', genero) };
  return { tom: 'info', icone: Icone.Negocio, rotulo: etapa?.trim() || flexionar('Abert{a}', genero) };
}

/**
 * A etapa do funil. `kind` manda: a etapa de fechamento do cliente pode se
 * chamar "Assinado" e continua ganho. O resto segue o vocabulário
 * (ui/vocabulario.ts, transcrito do documento de ativos) com o ícone do funil.
 */
export function etiquetaDeEtapa(nome: string | null | undefined, kind?: string | null): Etiqueta {
  if (kind === 'won') return etiquetaDeNegocio('won', nome);
  if (kind === 'lost') return etiquetaDeNegocio('lost', nome);
  const tomPorVariante: Record<VarianteDePilula, TomDeEtiqueta> = {
    '': 'neutral', 'pill--contorno': 'outline', 'pill--info': 'info', 'pill--atencao': 'warning',
    'pill--erro': 'danger', 'pill--ok': 'success', 'pill--marca': 'brand',
  };
  return { tom: tomPorVariante[varianteDaEtiqueta(nome)], icone: Icone.Pipeline, rotulo: nome?.trim() || 'Sem etapa' };
}

/**
 * "N dias" sem mexer. produto.html: "6 dias parada" é atenção; a partir de
 * 30 dias já deu problema (erro), porque o negócio esfriou de fato. Abaixo
 * de 14 a tela normalmente nem mostra, mas a função responde do mesmo jeito,
 * em contorno, para quem quiser exibir sempre.
 */
export function etiquetaDeParado(dias: number): Etiqueta {
  const rotulo = dias === 1 ? '1 dia' : `${dias} dias`;
  if (dias >= 30) return { tom: 'danger', icone: Icone.Cronometro, rotulo };
  if (dias >= 14) return { tom: 'warning', icone: Icone.Cronometro, rotulo };
  return { tom: 'outline', icone: Icone.Cronometro, rotulo };
}

export function etiquetaDePrioridade(prioridade: string | null | undefined): Etiqueta {
  const k = chave(prioridade);
  if (k === 'alta' || k === 'high' || k === 'urgente' || k === 'urgent') return { tom: 'danger', icone: Icone.Alta, rotulo: 'Alta' };
  if (k === 'baixa' || k === 'low') return { tom: 'neutral', icone: Icone.ChevronBaixo, rotulo: 'Baixa' };
  return { tom: 'info', icone: Icone.Alvo, rotulo: 'Normal' };
}

/* ------------------------------------------------------------------ */
/* Pessoas, papel, plano e cliente (Plataforma)                         */
/* ------------------------------------------------------------------ */

/**
 * O papel da pessoa na conta (core/permissions.ts). Dono é a marca amarela
 * com a coroa; quem administra é marca com escudo; quem vende ou atende é
 * marca com o próprio ícone; quem só lê é contorno com o olho.
 */
export function etiquetaDePapel(papel: string | null | undefined): Etiqueta {
  const k = chave(papel);
  const PAPEL: Record<string, Etiqueta> = {
    owner: { tom: 'amarelo', icone: Icone.Coroa, rotulo: 'Dono' },
    dono: { tom: 'amarelo', icone: Icone.Coroa, rotulo: 'Dono' },
    admin: { tom: 'brand', icone: Icone.Escudo, rotulo: 'Admin' },
    administrador: { tom: 'brand', icone: Icone.Escudo, rotulo: 'Admin' },
    manager: { tom: 'brand', icone: Icone.Escudo, rotulo: 'Gestor' },
    gestor: { tom: 'brand', icone: Icone.Escudo, rotulo: 'Gestor' },
    member: { tom: 'brand', icone: Icone.Usuario, rotulo: 'Vendedor' },
    vendedor: { tom: 'brand', icone: Icone.Usuario, rotulo: 'Vendedor' },
    seller: { tom: 'brand', icone: Icone.Usuario, rotulo: 'Vendedor' },
    agent: { tom: 'brand', icone: Icone.Atendente, rotulo: 'Atendente' },
    atendente: { tom: 'brand', icone: Icone.Atendente, rotulo: 'Atendente' },
    viewer: { tom: 'outline', icone: Icone.Olho, rotulo: 'Leitura' },
    leitura: { tom: 'outline', icone: Icone.Olho, rotulo: 'Leitura' },
    lider: { tom: 'brand', icone: Icone.MarcadorEstrela, rotulo: 'Líder' },
    yard: { tom: 'amarelo', icone: Icone.Escudo, rotulo: 'Yard' },
    voce: { tom: 'brand', icone: Icone.Usuario, rotulo: 'Você' },
  };
  return PAPEL[k] ?? { tom: 'brand', icone: Icone.Usuario, rotulo: papel ? capitalizar(String(papel)) : 'Sem papel' };
}

/**
 * O plano contratado. Pertence à plataforma: marca com a estrela. Período de
 * teste é atenção com o cronômetro, porque acaba; sem plano é contorno.
 * `nome` é o rótulo já traduzido pela tela (nomeDoPlano), quando ela tem um.
 */
export function etiquetaDePlano(plano: string | null | undefined, nome?: string): Etiqueta {
  const k = chave(plano);
  if (!k || k === 'none' || k === 'sem plano') return { tom: 'outline', icone: Icone.MarcadorEstrela, rotulo: 'Sem plano' };
  if (k === 'trial' || k === 'teste' || k === 'demo') return { tom: 'warning', icone: Icone.Cronometro, rotulo: nome ?? 'Teste' };
  if (k === 'free' || k === 'gratis' || k === 'gratuito') return { tom: 'outline', icone: Icone.MarcadorEstrela, rotulo: nome ?? 'Grátis' };
  const NOMES: Record<string, string> = { basic: 'Básico', pro: 'Pro', scale: 'Scale', enterprise: 'Enterprise' };
  return { tom: 'brand', icone: Icone.MarcadorEstrela, rotulo: nome ?? NOMES[k] ?? capitalizar(String(plano)) };
}

/**
 * A situação da organização no console: cliente (ativa), suspensa (já deu
 * problema, cadeado), demonstração/teste (vai acabar se ninguém agir).
 */
export function etiquetaDeCliente(status: string | null | undefined): Etiqueta {
  const k = chave(status);
  if (k === 'active' || k === 'ativo' || k === 'ativa' || k === 'cliente') return { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Cliente' };
  if (k === 'suspended' || k === 'suspenso' || k === 'suspensa') return { tom: 'danger', icone: Icone.Cadeado, rotulo: 'Suspenso' };
  if (k === 'churned' || k === 'cancelado' || k === 'cancelada' || k === 'encerrado') return { tom: 'escuro', icone: Icone.Arquivar, rotulo: 'Encerrado' };
  return { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Demonstração' };
}

/**
 * A situação de uma pessoa na conta: ativa, convidada (o convite ainda não
 * foi aceito, atenção), e-mail não confirmado, removida.
 */
export function etiquetaDeMembro(situacao: string | null | undefined, genero: Genero = 'm'): Etiqueta {
  const k = chave(situacao);
  if (k === 'active' || k === 'ativo' || k === 'ativa') return { tom: 'success', icone: Icone.CheckCirculo, rotulo: flexionar('Ativ{a}', genero) };
  if (k === 'invited' || k === 'convidado' || k === 'pending') return etiquetaDeStatus('invited');
  if (k === 'unconfirmed' || k === 'nao confirmado') return etiquetaDeStatus('unconfirmed');
  if (k === 'revoked' || k === 'removed' || k === 'removido') return { tom: 'escuro', icone: Icone.Cadeado, rotulo: flexionar('Removid{a}', genero) };
  if (k === 'suspended' || k === 'blocked') return etiquetaDeStatus('blocked', genero);
  return { tom: 'neutral', icone: Icone.Usuario, rotulo: situacao ? capitalizar(String(situacao)) : 'Sem situação' };
}

/* ------------------------------------------------------------------ */
/* Conversa, presença, sentimento e sincronia (Flow e Pop)              */
/* ------------------------------------------------------------------ */

/**
 * A situação de uma conversa (tela-flow.html §7: ícone + palavra, nunca cor
 * sozinha). "SLA estourado" é lido do relógio pela tela, não do banco.
 */
export function etiquetaDeConversa(status: string | null | undefined): Etiqueta {
  const k = chave(status).replace(/[_-]+/g, ' ');
  const CONVERSA: Record<string, Etiqueta> = {
    queued: { tom: 'neutral', icone: Icone.Fila, rotulo: 'Na fila' },
    'na fila': { tom: 'neutral', icone: Icone.Fila, rotulo: 'Na fila' },
    open: { tom: 'info', icone: Icone.Balao, rotulo: 'Aberta' },
    assigned: { tom: 'info', icone: Icone.Atendente, rotulo: 'Em atendimento' },
    'em atendimento': { tom: 'info', icone: Icone.Atendente, rotulo: 'Em atendimento' },
    waiting: { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Aguardando cliente' },
    'aguardando cliente': { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Aguardando cliente' },
    resolved: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Resolvida' },
    resolvida: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Resolvida' },
    resolvido: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Resolvida' },
    reopened: { tom: 'warning', icone: Icone.Reabrir, rotulo: 'Reaberta' },
    transferred: { tom: 'info', icone: Icone.Transferir, rotulo: 'Transferida' },
    transferido: { tom: 'info', icone: Icone.Transferir, rotulo: 'Transferida' },
    'sla breached': { tom: 'danger', icone: Icone.AlertaTriangulo, rotulo: 'SLA estourado' },
    'sla estourado': { tom: 'danger', icone: Icone.AlertaTriangulo, rotulo: 'SLA estourado' },
    bot: { tom: 'info', icone: Icone.Robo, rotulo: 'Com o robô' },
  };
  return CONVERSA[k] ?? { tom: 'neutral', icone: Icone.Balao, rotulo: status ? capitalizar(String(status)) : 'Sem situação' };
}

/** Presença do atendente: online ok, ausente atenção, em pausa contorno, offline neutro. */
export function etiquetaDePresenca(presenca: string | null | undefined): Etiqueta {
  const k = chave(presenca).replace(/[_-]+/g, ' ');
  if (k === 'online' || k === 'disponivel' || k === 'available') return { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Online' };
  if (k === 'ausente' || k === 'away') return { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Ausente' };
  if (k === 'em pausa' || k === 'pausa' || k === 'break') return { tom: 'outline', icone: Icone.Pausa, rotulo: 'Em pausa' };
  return { tom: 'neutral', icone: Icone.SemSinal, rotulo: 'Offline' };
}

/** Sentimento lido pela IA: positivo ok, neutro contorno, negativo atenção, urgente erro. */
export function etiquetaDeSentimento(sentimento: string | null | undefined): Etiqueta {
  const k = chave(sentimento);
  if (k === 'positive' || k === 'positivo') return { tom: 'success', icone: Icone.Satisfacao, rotulo: 'Positivo' };
  if (k === 'negative' || k === 'negativo') return { tom: 'warning', icone: Icone.AlertaTriangulo, rotulo: 'Negativo' };
  if (k === 'urgent' || k === 'urgente') return { tom: 'danger', icone: Icone.AlertaTriangulo, rotulo: 'Urgente' };
  return { tom: 'outline', icone: Icone.Reacao, rotulo: 'Neutro' };
}

/** A sincronia de um painel com o banco: sincronizado ok, sincronizando info, atrasado atenção, falha erro. */
export function etiquetaDeSincronia(estado: string | null | undefined): Etiqueta {
  const k = chave(estado);
  if (k === 'sincronizado' || k === 'synced' || k === 'ok') return { tom: 'success', icone: Icone.NuvemOk, rotulo: 'Sincronizado' };
  if (k === 'sincronizando' || k === 'syncing') return { tom: 'info', icone: Icone.Carregando, rotulo: 'Sincronizando' };
  if (k === 'atrasado' || k === 'stale') return { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Dado atrasado' };
  return { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Falha ao sincronizar' };
}

/* ------------------------------------------------------------------ */
/* Entrega de e-mail, pedido da loja, reunião e nível de acesso         */
/* ------------------------------------------------------------------ */

/**
 * O que aconteceu com um e-mail depois do envio. Entregue e aberto
 * terminaram bem; na fila está acontecendo; devolvido, spam e falha já deram
 * problema; sair da lista é atenção (a pessoa pediu para não receber).
 */
export function etiquetaDeEntrega(status: string | null | undefined): Etiqueta {
  const k = chave(status).replace(/[_-]+/g, ' ');
  const ENTREGA: Record<string, Etiqueta> = {
    entregue: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Entregue' },
    delivered: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Entregue' },
    enviado: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Entregue' },
    sent: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Entregue' },
    abriu: { tom: 'info', icone: Icone.Olho, rotulo: 'Abriu' },
    opened: { tom: 'info', icone: Icone.Olho, rotulo: 'Abriu' },
    clicou: { tom: 'info', icone: Icone.SetaDireita, rotulo: 'Clicou' },
    clicked: { tom: 'info', icone: Icone.SetaDireita, rotulo: 'Clicou' },
    fila: { tom: 'info', icone: Icone.Fila, rotulo: 'Na fila' },
    'na fila': { tom: 'info', icone: Icone.Fila, rotulo: 'Na fila' },
    queued: { tom: 'info', icone: Icone.Fila, rotulo: 'Na fila' },
    pendente: { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Pendente' },
    pending: { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Pendente' },
    devolvido: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Devolveu' },
    bounced: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Devolveu' },
    rejeitado: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Rejeitado' },
    rejected: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Rejeitado' },
    reclamado: { tom: 'danger', icone: Icone.Spam, rotulo: 'Marcou spam' },
    complained: { tom: 'danger', icone: Icone.Spam, rotulo: 'Marcou spam' },
    spam: { tom: 'danger', icone: Icone.Spam, rotulo: 'Marcou spam' },
    descadastrou: { tom: 'warning', icone: Icone.OlhoFechado, rotulo: 'Saiu da lista' },
    unsubscribed: { tom: 'warning', icone: Icone.OlhoFechado, rotulo: 'Saiu da lista' },
    falhou: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Falhou' },
    failed: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Falhou' },
  };
  return ENTREGA[k] ?? { tom: 'neutral', icone: Icone.EMail, rotulo: status ? capitalizar(String(status)) : 'Sem status' };
}

/**
 * A situação de um pedido da loja do Bio. Pago terminou bem; aguardando
 * pagamento é atenção; recusado é erro; expirado, reembolsado e cancelado
 * saem do fluxo (escuro, atenção e erro, respectivamente).
 */
export function etiquetaDePedido(status: string | null | undefined): Etiqueta {
  const k = chave(status).replace(/[_-]+/g, ' ');
  const PEDIDO: Record<string, Etiqueta> = {
    pending: { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Aguardando pagamento' },
    'aguardando pagamento': { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Aguardando pagamento' },
    paid: { tom: 'success', icone: Icone.Pagamento, rotulo: 'Pago' },
    pago: { tom: 'success', icone: Icone.Pagamento, rotulo: 'Pago' },
    shipped: { tom: 'info', icone: Icone.Entrega, rotulo: 'Enviado' },
    delivered: { tom: 'success', icone: Icone.Entrega, rotulo: 'Entregue' },
    rejected: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Recusado' },
    recusado: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Recusado' },
    expired: { tom: 'escuro', icone: Icone.Cronometro, rotulo: 'Expirado' },
    refunded: { tom: 'warning', icone: Icone.MoedaReal, rotulo: 'Reembolsado' },
    canceled: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Cancelado' },
    cancelled: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Cancelado' },
  };
  return PEDIDO[k] ?? { tom: 'neutral', icone: Icone.Pedido, rotulo: status ? capitalizar(String(status)) : 'Sem situação' };
}

/**
 * Reunião ou agendamento: agendada e confirmada ainda vão acontecer (info e
 * ok), ao vivo é agora, concluída terminou bem, cancelada é erro, faltou é
 * atenção (alguém precisa remarcar).
 */
export function etiquetaDeReuniao(status: string | null | undefined, genero: Genero = 'f'): Etiqueta {
  const k = chave(status).replace(/[_-]+/g, ' ');
  const REUNIAO: Record<string, ModeloDeStatus> = {
    agendada: { tom: 'info', icone: Icone.Calendario, rotulo: 'Agendad{a}' },
    agendado: { tom: 'info', icone: Icone.Calendario, rotulo: 'Agendad{a}' },
    scheduled: { tom: 'info', icone: Icone.Calendario, rotulo: 'Agendad{a}' },
    pendente: { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Aguardando confirmação' },
    pending: { tom: 'warning', icone: Icone.Cronometro, rotulo: 'Aguardando confirmação' },
    confirmada: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Confirmad{a}' },
    confirmado: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Confirmad{a}' },
    confirmed: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Confirmad{a}' },
    'em andamento': { tom: 'info', icone: Icone.SalaVideo, rotulo: 'Ao vivo' },
    live: { tom: 'info', icone: Icone.SalaVideo, rotulo: 'Ao vivo' },
    'ao vivo': { tom: 'info', icone: Icone.SalaVideo, rotulo: 'Ao vivo' },
    concluida: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Concluíd{a}' },
    concluido: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Concluíd{a}' },
    done: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Concluíd{a}' },
    realizada: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Realizad{a}' },
    realizado: { tom: 'success', icone: Icone.CheckCirculo, rotulo: 'Realizad{a}' },
    cancelada: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Cancelad{a}' },
    cancelado: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Cancelad{a}' },
    canceled: { tom: 'danger', icone: Icone.ErroCirculo, rotulo: 'Cancelad{a}' },
    faltou: { tom: 'warning', icone: Icone.AlertaTriangulo, rotulo: 'Faltou' },
    'no show': { tom: 'warning', icone: Icone.AlertaTriangulo, rotulo: 'Faltou' },
    passada: { tom: 'escuro', icone: Icone.Calendario, rotulo: 'Passad{a}' },
  };
  const m = REUNIAO[k];
  if (m) return { tom: m.tom, icone: m.icone, rotulo: flexionar(m.rotulo, genero) };
  return { tom: 'neutral', icone: Icone.Calendario, rotulo: status ? capitalizar(String(status)) : 'Sem situação' };
}

/**
 * O nível de acesso a um documento ou link: público (globo, info), restrito
 * (cadeado, atenção), privado (cadeado, neutro), equipe (contorno), e os
 * links mortos (expirado, esgotado) em escuro.
 */
export function etiquetaDeNivelDeAcesso(nivel: string | null | undefined): Etiqueta {
  const k = chave(nivel).replace(/[_-]+/g, ' ');
  const NIVEL: Record<string, Etiqueta> = {
    publico: { tom: 'info', icone: Icone.Globo, rotulo: 'Link público' },
    public: { tom: 'info', icone: Icone.Globo, rotulo: 'Link público' },
    'link publico': { tom: 'info', icone: Icone.Globo, rotulo: 'Link público' },
    restrito: { tom: 'warning', icone: Icone.Cadeado, rotulo: 'Restrito' },
    restricted: { tom: 'warning', icone: Icone.Cadeado, rotulo: 'Restrito' },
    privado: { tom: 'neutral', icone: Icone.Cadeado, rotulo: 'Privado' },
    private: { tom: 'neutral', icone: Icone.Cadeado, rotulo: 'Privado' },
    equipe: { tom: 'outline', icone: Icone.Participantes, rotulo: 'Equipe' },
    team: { tom: 'outline', icone: Icone.Participantes, rotulo: 'Equipe' },
    compartilhado: { tom: 'outline', icone: Icone.PastaCompartilhada, rotulo: 'Compartilhado' },
    expirado: { tom: 'escuro', icone: Icone.Cronometro, rotulo: 'Expirado' },
    esgotado: { tom: 'escuro', icone: Icone.Cronometro, rotulo: 'Esgotado' },
    bastidor: { tom: 'warning', icone: Icone.OlhoFechado, rotulo: 'Bastidor' },
  };
  return NIVEL[k] ?? { tom: 'neutral', icone: Icone.Cadeado, rotulo: nivel ? capitalizar(String(nivel)) : 'Privado' };
}
